/**
 * @file CalendarWidget.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-02-02
 * @modified 2026-10-03
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import MonthCalendar from '../components/MonthCalendar';
import { API_URL } from '../config';

// The shared office calendar (Exchange, read by the server) is re-read every
// five minutes while the widget is on screen, and whenever the month changes.
const REFRESH_MS = 5 * 60 * 1000;

function CalendarWidget() {
  const [range, setRange] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const latest = useRef(0);

  const onRangeChange = useCallback((start, end) => {
    setRange((r) => (r && +r.start === +start && +r.end === +end ? r : { start, end }));
  }, []);

  useEffect(() => {
    if (!range) return undefined;
    const load = async () => {
      const ticket = ++latest.current;
      try {
        const qs = `start=${encodeURIComponent(range.start.toISOString())}&end=${encodeURIComponent(range.end.toISOString())}`;
        const res = await fetch(`${API_URL}/api/calendar/events?${qs}`, { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (ticket !== latest.current) return;
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        setEvents(Array.isArray(data.events) ? data.events : []);
        setError(null);
      } catch (e) {
        if (ticket === latest.current) setError(e.message);
      }
    };
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [range]);

  return (
    <div className="dashboard-card calendar-card">
      <div className="card-body">
        <MonthCalendar events={events} onRangeChange={onRangeChange} error={error} />
      </div>
    </div>
  );
}

export default CalendarWidget;
